/**
 * Remap proposed snapshot node IDs to current draft node IDs by
 * (nodeType, orderIndex) when callers submit a structurally identical
 * snapshot with re-minted UUIDs.
 *
 * Why this exists: the diff engine in @chatsdet/workflow-diff matches nodes by
 * `id` only. Callers like `canary workflow sync --propose` historically minted
 * fresh UUIDs for every node (because agents passed in just `{nodeType,
 * configJson}`), which made every proposal read as a full
 * remove-everything/add-everything rewrite even when the structure was
 * identical. This pass canonicalizes IDs at the write boundary so the diff
 * stays pure and downstream consumers (compose-snapshot, partial accept, the
 * review UI) all keep matching by id.
 *
 * The rule is intentionally conservative: a proposed node only inherits a
 * current node's id when both the nodeType and the orderIndex line up. That
 * matches the common "agent rewrote configs but kept the shape" case without
 * collapsing genuine reorders/insertions/deletions into false matches.
 */
export interface AlignNode {
  id: string;
  nodeType: string;
  orderIndex?: number;
  // configJson is intentionally unknown — the aligner doesn't read it. Keeping
  // it permissive lets callers pass straight through from drizzle rows
  // (`unknown`-typed jsonb) without an explicit cast.
  configJson?: unknown;
}

export interface AlignEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType?: string | null;
}

export interface AlignSnapshot<N extends AlignNode = AlignNode, E extends AlignEdge = AlignEdge> {
  nodes: N[];
  edges: E[];
}

export interface AlignResult<S extends AlignSnapshot> {
  snapshot: S;
  /** Number of proposed nodes whose id was remapped to a current node id. */
  remappedCount: number;
}

/**
 * Return a new snapshot whose node IDs (and edge endpoint IDs) are remapped
 * to existing current-draft IDs whenever a proposed node clearly corresponds
 * to a current one.
 *
 * - If a proposed node's id already exists in the current draft, keep it.
 * - Else, look for an unmatched current node with the same nodeType at the
 *   same orderIndex. If found, remap the proposed id to that current id.
 * - Else, leave the proposed id as-is (it's a genuinely new node).
 *
 * Edges are rewritten to reference the remapped node IDs. Edge IDs are kept
 * as-is because the diff engine matches edges by source+target+type, not by
 * edge id.
 *
 * Pure: the input snapshot is not mutated.
 */
export function alignSnapshotNodeIds<S extends AlignSnapshot>(
  proposed: S,
  currentNodes: ReadonlyArray<AlignNode>,
): AlignResult<S> {
  const currentIds = new Set<string>();
  // Key: `${nodeType}@${orderIndex}` — orderIndex is required for slot matching.
  const slotMap = new Map<string, string>();
  for (const n of currentNodes) {
    currentIds.add(n.id);
    if (typeof n.orderIndex === 'number') {
      slotMap.set(`${n.nodeType}@${n.orderIndex}`, n.id);
    }
  }

  const consumed = new Set<string>();
  const remap = new Map<string, string>();
  let remappedCount = 0;

  const nodes = proposed.nodes.map((node) => {
    if (currentIds.has(node.id) && !consumed.has(node.id)) {
      consumed.add(node.id);
      return node;
    }
    if (typeof node.orderIndex === 'number') {
      const slotKey = `${node.nodeType}@${node.orderIndex}`;
      const candidate = slotMap.get(slotKey);
      if (candidate && !consumed.has(candidate)) {
        consumed.add(candidate);
        if (candidate !== node.id) {
          remap.set(node.id, candidate);
          remappedCount += 1;
        }
        return { ...node, id: candidate };
      }
    }
    return node;
  });

  const edges = proposed.edges.map((edge) => {
    const newSource = remap.get(edge.sourceNodeId);
    const newTarget = remap.get(edge.targetNodeId);
    if (newSource === undefined && newTarget === undefined) return edge;
    return {
      ...edge,
      sourceNodeId: newSource ?? edge.sourceNodeId,
      targetNodeId: newTarget ?? edge.targetNodeId,
    };
  });

  return {
    snapshot: { ...proposed, nodes, edges } as S,
    remappedCount,
  };
}
